// @ts-check

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  ChoiceList,
  EmptySearchResult,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
  InlineStack,
  Popover,
  Spinner,
  Text,
  TextField,
  Tooltip,
  useBreakpoints,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import { LayoutColumns3Icon } from "@shopify/polaris-icons";
import { useSearchParams } from "react-router-dom";

import { OrdersTableRow } from "components/OrdersTable/OrdersTableRow";

import { useGlobalContext } from "context/GlobalContext";

import { ORDERS_PAGE_SIZE } from "utils/orders";
import {
  FILTER_FIELDS,
  getStartingColumnFiltersText,
} from "utils/tables";

import {
  getStartingTableColumns,
  TABLE_COLUMNS,
} from "./utils";

export const OrdersTable = () => {
  const {
    orders,
    isFetchingOrders,
    setOrdersPage,
    ordersPage,
    ordersTotalCount,
    ordersQueryTerm,
    setOrdersQueryTerm,
    ordersSortSelected,
    setOrdersSortSelected,
  } = useGlobalContext();
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);
  const [searchParams, setSearchParams] = useSearchParams();

  const [tabIndex, setTabIndex] = useState(0);
  const [editColumnsPopoverActive, setEditColumnsPopoverActive] = useState(false);
  const [columns, setColumns] = useState(getStartingTableColumns());
  const [columnFiltersText, setColumnFiltersText] = useState(getStartingColumnFiltersText());

  const ordersActiveColumnFilters = useMemo(() => {
    return FILTER_FIELDS
      .map((field) => {
        return {
          ...field,
          filter: searchParams.get(field.key)
        };
      })
      .filter(({ filter }) => Boolean(filter));
  }, [searchParams]);

  const tabs = [
    {
      content: "All",
      index: 0,
      onAction: () => setTabIndex(0),
      id: "all",
    },
  ];

  // start in filters mode if there are active filters
  useEffect(() => {
    if (!isFetchingOrders && ordersActiveColumnFilters.length) {
      setMode(IndexFiltersMode.Filtering);
    }
  }, [isFetchingOrders, ordersActiveColumnFilters]);

  // keep filter input values in sync with active filters
  useEffect(() => {
    const activeFilters = ordersActiveColumnFilters.reduce((prev, curr) => {
      return {
        ...prev,
        [curr.key]: curr.filter,
      };
    }, {});

    setColumnFiltersText(currentFilters => ({ ...currentFilters, ...activeFilters }));
  }, [ordersActiveColumnFilters]);

  const handleClearAll = () => {
    setOrdersQueryTerm("");
    setSearchParams({});
    setColumnFiltersText(getStartingColumnFiltersText());
  };

  const handleCancel = () => {
    setOrdersQueryTerm("");
    setMode(IndexFiltersMode.Default);
  };

  const handleColumnsChange = (value) => {
    if (!value.length) {
      return;
    }

    return setColumns(value);
  };

  const togglePopoverActive = () => {
    setEditColumnsPopoverActive(active => !active);
  };

  const setFilterValue = (filterKey, value) => {
    const currentFilters = ordersActiveColumnFilters
      .reduce((prev, curr) => {
        return {
          ...prev,
          [curr.key]: curr.filter,
        };
      }, {});

    setSearchParams({
      ...currentFilters,
      [filterKey]: value,
    });

    // to ensure the active filter values update we need to switch to default
    // mode while loading, the useEffect hook above will switch back to
    // filtering mode when loading has finished
    setMode(IndexFiltersMode.Default);
  };

  const removeFilter = (filterKey) => {
    const currentFilters = ordersActiveColumnFilters
      .filter(({ key }) => key !== filterKey)
      .reduce((prev, curr) => {
        return {
          ...prev,
          [curr.key]: curr.filter,
        };
      }, {});

    setSearchParams(currentFilters);
  };

  const filters = FILTER_FIELDS.map(({ key, label }) => ({
    key: key,
    label: label,
    filter: (
      <TextField
        type="text"
        label={label}
        value={columnFiltersText[key]}
        onChange={(value) => setColumnFiltersText(filtersText => ({ ...filtersText, [key]: value }))}
        autoComplete="off"
        connectedRight={
          <Button
            onClick={() => setFilterValue(key, columnFiltersText[key])}
          >
            Save
          </Button>
        }
      />
    ),
    shortcut: true,
  }));

  const appliedFilters = ordersActiveColumnFilters
    .map(({ key, label, filter }) => ({
      key,
      label: `${label}: ${filter}`,
      onRemove: () => {
        removeFilter(key);
      },
    }));

  const sortedValues = TABLE_COLUMNS.map((col) => col.value);

  const headings = TABLE_COLUMNS
    .slice()
    .filter(({ value }) => columns.includes(value))
    .sort((a, b) => sortedValues.indexOf(a.value) - sortedValues.indexOf(b.value))
    .map((column) => {
      return {
        id: column.label,
        alignment: column.numeric || column.value === "actions" ? "end" : "start",
        title: column.label,
      };
    });

  const emptyStateMarkup = (
    <EmptySearchResult
      title="No orders yet"
      description="Try reloading the orders or editing the search/filters"
      withIllustration
    />
  );

  const editColumnsActivator = (
    <Tooltip content="Edit columns" dismissOnMouseOut>
      <Button
        size="slim"
        icon={LayoutColumns3Icon}
        onClick={togglePopoverActive}
      />
    </Tooltip>
  );

  const totalPages = ~~(ordersTotalCount / ORDERS_PAGE_SIZE) + 1;

  const paginationLabel = (
    <InlineStack gap="300" align="center">
      <Text fontWeight="semibold" as="p">Page {ordersPage} of {totalPages}</Text>
      <Text as="p">Showing {orders.length} of {new Intl.NumberFormat("en-US").format(ordersTotalCount)} orders</Text>
      {isFetchingOrders && <Spinner size="small" />}
    </InlineStack>
  );

  let columnFilter;
  if (useBreakpoints().smUp) {
    columnFilter = (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingRight: "var(--p-space-200)",
          borderBottom: "var(--p-border-width-025) solid var(--p-color-border)",
        }}
      >
        <Popover
          active={editColumnsPopoverActive}
          activator={editColumnsActivator}
          autofocusTarget="first-node"
          onClose={togglePopoverActive}
        >
          <Box padding="400">
            <ChoiceList
              allowMultiple
              title="Columns"
              choices={TABLE_COLUMNS.filter((option) => !["order #", "date", "total"].includes(option.value))}
              selected={columns}
              onChange={handleColumnsChange}
            />
          </Box>
        </Popover>
      </div>
    );
  }

  const sortOptions = [
    { label: "Date", value: "date asc", directionLabel: "Ascending" },
    { label: "Date", value: "date desc", directionLabel: "Descending" },
  ];

  return (
    <Card padding="0">
      <div style={{ display: "flex", flexDirection: "row" }}>
        <IndexFilters
          sortOptions={sortOptions}
          sortSelected={ordersSortSelected}
          onSort={setOrdersSortSelected}
          queryValue={ordersQueryTerm}
          queryPlaceholder="Search by order number"
          onQueryChange={setOrdersQueryTerm}
          onQueryClear={() => setOrdersQueryTerm("")}
          cancelAction={{
            onAction: handleCancel,
            disabled: false,
            loading: false,
          }}
          tabs={tabs}
          selected={tabIndex}
          loading={isFetchingOrders}
          onClearAll={handleClearAll}
          mode={mode}
          setMode={setMode}
          filters={filters}
          appliedFilters={appliedFilters}
          canCreateNewView={false}
        />
        {columnFilter}
      </div>

      <IndexTable
        condensed={useBreakpoints().smDown}
        resourceName={{ singular: "order", plural: "orders" }}
        itemCount={orders.length}
        selectable={false}
        emptyState={emptyStateMarkup}
        headings={headings}
        lastColumnSticky
        pagination={{
          label: paginationLabel,
          hasPrevious: ordersPage > 1,
          hasNext: ORDERS_PAGE_SIZE * ordersPage < ordersTotalCount,
          onNext: () => {
            setOrdersPage(page => Math.min(totalPages, page + 1));
          },
          onPrevious: () => {
            setOrdersPage(page => Math.max(0, page - 1));
          },
        }}
      >
        {orders.map((row, index) => (
          <OrdersTableRow
            key={index}
            index={index}
            data={row}
            columns={columns}
            sortedValues={sortedValues}
            setFilterValue={setFilterValue}
            removeFilter={removeFilter}
          />
        ))}
      </IndexTable>
    </Card>
  );
};
